/**
 * v2.97z — Layer 2: Subtle Trial Nudge ribbon.
 *
 * Slim bar that appears at the TOP of the viewport once the user has shown
 * engagement signals on this session:
 *   - Scrolled past 3 rails (~1800px) OR
 *   - Spent >45s on the page
 *
 * Hidden for:
 *   - Logged-in users (they already have the existing PaywallModal trial CTA)
 *   - Pro / Founder / Trial users (already paying or trialing)
 *   - Users who have permanently dismissed it
 *   - Users who saw it less than 7 days ago
 *
 * Re-eligibility: once every 7 days unless "Don't show again" was clicked.
 *
 * Storage keys:
 *   `fm.subtleTrialNudge.lastSeenAt`        ISO timestamp
 *   `fm.subtleTrialNudge.permanentlyHidden` "1" | absent
 *
 * Also listens for the `fm-subtle-trial-dismissed` event so Layer 3
 * (watchlist hook) can suppress itself in the same session.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useSubscription } from "../lib/SubscriptionContext";

const LS_LAST_SEEN = "fm.subtleTrialNudge.lastSeenAt";
const LS_PERMANENT = "fm.subtleTrialNudge.permanentlyHidden";
const REAPPEAR_AFTER_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days
const TIME_THRESHOLD_MS = 45_000;                     // 45s on page
const SCROLL_THRESHOLD_PX = 1800;                     // ~3 rails

export default function SubtleTrialRibbon() {
  const { user } = useAuth();
  const { tier, loading } = useSubscription();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user?.user_id) return;           // logged in → other CTAs handle this
    if (tier !== "guest") return;        // trial/pro/founder → skip
    try {
      if (localStorage.getItem(LS_PERMANENT) === "1") return;
      const lastSeen = localStorage.getItem(LS_LAST_SEEN);
      if (lastSeen) {
        const lastTs = new Date(lastSeen).getTime();
        if (!Number.isNaN(lastTs) && Date.now() - lastTs < REAPPEAR_AFTER_MS) {
          return;
        }
      }
    } catch (_e) { return; }

    let timeFired = false;
    let scrollFired = false;
    const maybeShow = () => {
      if (timeFired || scrollFired) setVisible(true);
    };
    const onScroll = () => {
      if (window.scrollY >= SCROLL_THRESHOLD_PX) {
        scrollFired = true;
        maybeShow();
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(() => {
      timeFired = true;
      maybeShow();
    }, TIME_THRESHOLD_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [loading, user?.user_id, tier]);

  const markSeen = (permanent = false) => {
    try {
      localStorage.setItem(LS_LAST_SEEN, new Date().toISOString());
      if (permanent) localStorage.setItem(LS_PERMANENT, "1");
      // Session-scoped suppressor read by Layer 3 (MovieCard.jsx onSave).
      sessionStorage.setItem("fm.subtleTrialJustDismissed", "1");
    } catch (_e) { /* ignore */ }
    // Tell Layer 3 (watchlist hook) to back off this session.
    window.dispatchEvent(new CustomEvent("fm-subtle-trial-dismissed"));
  };

  const handleStart = () => { markSeen(); setVisible(false); navigate("/login?next=/?trial=1"); };
  const handleNotNow = () => { markSeen(); setVisible(false); };
  const handlePermanent = () => { markSeen(true); setVisible(false); };

  if (!visible) return null;

  return (
    <div
      data-testid="subtle-trial-ribbon"
      className="fixed top-[60px] inset-x-0 z-[55] mx-auto max-w-screen-xl px-4"
    >
      <div className="rounded-full border border-amber-400/30 bg-gradient-to-r from-amber-400/10 via-amber-500/5 to-orange-400/10 backdrop-blur-md shadow-lg px-4 py-2 flex items-center gap-3 text-sm">
        <Sparkles className="w-4 h-4 text-amber-300 shrink-0" />
        <div className="flex-1 text-white/90 truncate">
          <span className="font-semibold text-amber-200">Try Filmistani Pro free for 7 days</span>
          <span className="text-white/60"> — no card needed</span>
        </div>
        <button
          onClick={handleStart}
          data-testid="subtle-trial-start"
          className="px-3 py-1 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-semibold transition shrink-0"
        >
          Start free trial
        </button>
        <button
          onClick={handleNotNow}
          data-testid="subtle-trial-notnow"
          className="text-xs text-neutral-400 hover:text-white transition shrink-0"
        >
          Not now
        </button>
        <button
          onClick={handlePermanent}
          aria-label="Don't show again"
          data-testid="subtle-trial-dismiss"
          className="text-neutral-500 hover:text-white transition shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
